<template>
    <button class="btn" type="button"
        :class="[getButtonColor, getButtonSize, getFullWidth, getTextAndIcons, getProcessing, getLoopAnimation]"
        :disabled="disabled">

        <i v-if="isButtonIconVisible" :class="buttonIconClasses" aria-hidden="true"></i>

        <svg v-if="processing" class="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none"
            viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
            </path>
        </svg>

        <span v-if="loopAnimation" class="icon-right"
            :class="loopDirection == 'right' ? 'group-hover:translate-x-0' : 'group-hover:translate-x-8'">
            <i class="ri-arrow-right-line" aria-hidden="true"></i>
        </span>
        <span v-if="loopAnimation" class="icon-left"
            :class="loopDirection == 'right' ? 'group-hover:-translate-x-8' : 'group-hover:translate-x-0'">
            <i class="ri-arrow-right-line" aria-hidden="true"></i>
        </span>

        <span :class="[{ 'sr-only': hasOnlyIcon }, { 'loop-text': loopAnimation }]">{{ text }}</span>

        <i v-if="hasTextAndIcon && rightIcon" class="ml-2" :class="rightIcon" aria-hidden="true"></i>
    </button>
</template>

<script lang="ts">
import { defineComponent } from "vue";

export default defineComponent({
    name: 'StandardButton',
    props: {
        text: {
            type: String,
            required: true,
            default: ''
        },
        color: {
            type: String,
            default: ''
        },
        size: {
            type: String,
            default: ''
        },
        isFullWidth: {
            default: false
        },
        hasOnlyIcon: {
            default: false
        },
        onlyIconClass: {
            default: ''
        },
        hasTextAndIcon: {
            default: false
        },
        leftIcon: {
            type: String,
            default: ''
        },
        rightIcon: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false
        },
        processing: {
            type: Boolean,
            default: false
        },
        loopAnimation: {
            type: Boolean,
            default: false
        },
        loopDirection: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            buttonIcon: '',
            buttonIconClasses: '',
            isButtonIconVisible: false,
            isDisabled: false
        }
    },
    computed: {
        getFullWidth() {
            return this.isFullWidth && 'justify-center w-full';
        },

        getTextAndIcons() {
            return (this.hasTextAndIcon || this.hasOnlyIcon) && 'btn-w-icon';
        },

        getProcessing() {
            return this.processing && 'btn-processing';
        },

        getLoopAnimation() {
            const loopDirection = this.loopDirection?.toLowerCase();
            let loopClasses = 'btn-loop group';

            if (this.loopAnimation) {
                loopDirection == 'left' && (loopClasses += ' left-loop');
                loopDirection == 'right' && (loopClasses += ' right-loop');

                return loopClasses;
            }
            else return false;
        },

        getButtonSize() {
            const buttonSize = this.size?.toLowerCase();

            return {
                'btn-sm': (buttonSize == 'small'),
                'btn-lg': (buttonSize == 'large'),
            }
        },

        getButtonColor() {
            const buttonColor = this.color?.toLowerCase();

            switch (buttonColor) {
                case 'secondary':
                    return 'btn-secondary-green';
                case 'neutral':
                    return 'btn-neutral';
                case 'borderless primary':
                    return 'btn-borderless-green';
                case 'borderless neutral':
                    return 'btn-borderless-neutral';
                case 'action primary':
                    return 'btn-action-primary';
                case 'action secondary':
                    return 'btn-action-secondary';
                case 'action neutral':
                    return 'btn-action-neutral';
                default:
                    return 'btn-primary-green';
            }
        },
    },
    methods: {
        load() {
            if ((this.hasTextAndIcon && this.leftIcon) || this.hasOnlyIcon) {
                this.isButtonIconVisible = true;
            }

            if (this.hasOnlyIcon) {
                this.buttonIconClasses = this.onlyIconClass;
            }
            else if (this.hasTextAndIcon && this.leftIcon) {
                this.buttonIconClasses = this.leftIcon + ' mr-2';
            }

            this.isDisabled = this.disabled;
        }
    },
    mounted() {
        this.load();
    },
    watch: {
        disabled: {
            handler(newVal: boolean) {
                this.isDisabled = newVal;
            }
        }
    }
});
</script>
